import React, { ForwardedRef } from 'react';

import cn from 'classnames';

import styles from './Button.module.css';
import { Spinner } from '../spinner/Spinner';

export type ButtonProps = {
  variant?:
    | 'primary'
    | 'secondary'
    | 'secondary-alt'
    | 'tertiary'
    | 'success'
    | 'action'
    | 'denim';
  size?: 'small' | 'medium' | 'large';
  /** @deprecated */
  iconOnly?: boolean;
  href?: string;
  disabled?: boolean;
  loading?: boolean;
  /** @deprecated */
  noShadow?: boolean;
} & (
  | React.AnchorHTMLAttributes<HTMLAnchorElement>
  | React.ButtonHTMLAttributes<HTMLButtonElement>
);

export const Button = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    {
      className,
      href,
      variant = 'primary',
      size = 'medium',
      iconOnly,
      children,
      disabled,
      loading = false,
      noShadow = false,
      ...props
    },
    ref,
  ) => {
    if (iconOnly) {
      console.warn('iconOnly is deprecated');
    }
    if (noShadow) {
      console.warn('noShadow is deprecated');
    }

    const classNames = cn(
      styles.button,
      styles[variant],
      styles[size],
      { [styles.iconOnly]: iconOnly },
      { [styles.disabled]: disabled || loading },
      { [styles.loading]: loading },
      { [styles.noShadow]: noShadow },
      className,
    );

    if (href !== undefined) {
      return (
        <a
          {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
          href={href}
          className={classNames}
          ref={ref as ForwardedRef<HTMLAnchorElement>}
          aria-busy={loading}
        >
          <>
            {loading && <Spinner className={styles.spinner} size={size} />}
            {children}
          </>
        </a>
      );
    }

    return (
      <button
        {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
        className={classNames}
        ref={ref as ForwardedRef<HTMLButtonElement>}
        disabled={disabled || loading}
        aria-busy={loading}
      >
        {loading && (
          <Spinner
            className={styles.spinner}
            size={size}
            dataTestId="button-loading"
          />
        )}
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';
